import { readActivationContext, recordSoftphoneDiagnostic } from './diagnostics'

/**
 * Ringtone playback. The tone is generated in-memory so no binary asset is added to the repo.
 */
const SAMPLE_RATE = 8000

// Two tones a major third apart, played together. A phone's small speaker is weak at low pitches:
// the earlier single 480 Hz tone sounded faint and thin on a phone even at full media volume
// (docs/softphone-mobile-plan.md §11). Higher tones carry, and the consonant interval keeps the
// chord from sounding like an alarm. 880/1100 Hz carried well but was judged a little shrill on a
// phone, so the chord sits a few semitones lower.
const TONE_FREQUENCIES_HZ = [700, 875]

// The peak level of each tone. Together they peak at 0.7 of full scale, which leaves headroom.
const TONE_AMPLITUDE = 0.35

// A double ring: two short bursts, then a pause, the cadence of a phone ringing in the UK and much of
// the world. A short beep on a one-second loop sounded like a notification, which is the one thing an
// inbound call must not be mistaken for. The double burst is unmistakably a phone.
const CADENCE = [
  { tone: true, seconds: 0.4 },
  { tone: false, seconds: 0.2 },
  { tone: true, seconds: 0.4 },
  { tone: false, seconds: 2 }
]

// The tone is faded in and out over a few milliseconds. Starting or stopping a sine wave at full
// amplitude puts a step in the waveform, which is audible as a click on every loop.
const FADE_SECONDS = 0.005

let audioElement = null
let isArmed = false

function buildToneDataUri() {
  const segments = CADENCE.map(segment => ({ ...segment, samples: Math.round(SAMPLE_RATE * segment.seconds) }))
  const sampleCount = segments.reduce((total, segment) => total + segment.samples, 0)
  const fadeSampleCount = Math.max(1, Math.round(SAMPLE_RATE * FADE_SECONDS))
  const bytes = new Uint8Array(44 + sampleCount)
  const view = new DataView(bytes.buffer)

  const writeAscii = (offset, text) => {
    for (let i = 0; i < text.length; i++) {
      view.setUint8(offset + i, text.charCodeAt(i))
    }
  }

  writeAscii(0, 'RIFF')
  view.setUint32(4, 36 + sampleCount, true)
  writeAscii(8, 'WAVEfmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, SAMPLE_RATE, true)
  view.setUint32(28, SAMPLE_RATE, true)
  view.setUint16(32, 1, true)
  view.setUint16(34, 8, true)
  writeAscii(36, 'data')
  view.setUint32(40, sampleCount, true)

  let offset = 0

  segments.forEach(segment => {
    for (let i = 0; i < segment.samples; i++) {
      if (!segment.tone) {
        view.setUint8(44 + offset + i, 128)

        continue
      }

      // Each burst fades in and out on its own, so neither its start nor its end clicks.
      const fade = Math.min(1, (i + 1) / fadeSampleCount, (segment.samples - i) / fadeSampleCount)
      const time = i / SAMPLE_RATE
      const sample = TONE_FREQUENCIES_HZ
        .reduce((sum, hz) => sum + Math.sin(2 * Math.PI * hz * time), 0) * TONE_AMPLITUDE * fade

      view.setUint8(44 + offset + i, 128 + Math.round(sample * 127))
    }

    offset += segment.samples
  })

  let binary = ''
  bytes.forEach(byte => { binary += String.fromCharCode(byte) })

  return `data:audio/wav;base64,${btoa(binary)}`
}

function getAudioElement() {
  if (typeof Audio === 'undefined') {
    return null
  }

  if (!audioElement) {
    audioElement = new Audio(buildToneDataUri())
    audioElement.loop = true
  }

  return audioElement
}

/**
 * Browsers refuse to play audio until the user has interacted with the page, and an inbound
 * call arrives with no gesture of its own. Going online IS a click, so unlock the element
 * there by starting and immediately stopping it; later `startRinging` calls are then allowed.
 */
export function armRingtone() {
  const audio = getAudioElement()

  if (!audio) {
    recordSoftphoneDiagnostic('ringtone', 'ringtone_arm', { result: 'unavailable', error: null })

    return Promise.resolve(false)
  }

  audio.muted = true

  // Read before `play()`: user activation can expire before the promise settles, and a refusal is
  // explained by what was true when it was asked for.
  const context = readActivationContext()

  return Promise.resolve(audio.play())
    .then(() => {
      audio.pause()
      audio.currentTime = 0
      audio.muted = false
      isArmed = true

      recordSoftphoneDiagnostic('ringtone', 'ringtone_arm', { result: 'armed', error: null, ...context })

      return true
    })
    .catch(error => {
      audio.muted = false

      recordSoftphoneDiagnostic('ringtone', 'ringtone_arm', { result: 'refused', error: error?.name || null, ...context })

      return false
    })
}

export function isRingtoneArmed() {
  return isArmed
}

export function startRinging() {
  const audio = getAudioElement()

  if (!audio) {
    return
  }

  audio.currentTime = 0

  const context = readActivationContext()

  Promise.resolve(audio.play()).then(() => {
    recordSoftphoneDiagnostic('ringtone', 'ringtone_play', { result: 'started', error: null, ...context })
  }, error => {
    // Autoplay still blocked (agent never clicked). The visual ringing state is the fallback.
    recordSoftphoneDiagnostic('ringtone', 'ringtone_play', { result: 'refused', error: error?.name || null, ...context })
  })
}

export function stopRinging() {
  if (!audioElement) {
    return
  }

  audioElement.pause()
  audioElement.currentTime = 0
}

export function disposeRingtone() {
  stopRinging()

  audioElement = null
  isArmed = false
}
